/*
  Robust convertion of string to INode[]

*/
export const NODE_TYPES = ['div', 'ol', 'ul', 'li', 'h1', 'h2', 'h3'] as const;
export type NodeType = (typeof NODE_TYPES)[number];
export const NODE_TYPE_SET = new Set<NodeType>(NODE_TYPES);

export interface INode {
  text?: string;
  type: NodeType;
  // Always an array; no scalar.
  ref?: Array<string | number>;
  children?: INode[];
}

// --- Core: tolerant salvage + parse ---

/**
 * Returns the longest valid JSON prefix that ends right after a complete value,
 * then appends the necessary closing brackets/braces for any parents that were open
 * at that point (dropping the incomplete trailing item in a child array).
 */
export function parseValidPromptBlocks(input: string): INode[] {
  // Fast path
  try {
    return normalizeParsedNodes(JSON.parse(input));
  } catch {}

  // Try to salvage by finding the last safe boundary and closing parents
  const repaired = salvageJsonPrefix(input);
  if (repaired) {
    try {
      return normalizeParsedNodes(JSON.parse(repaired));
    } catch {}
  }

  // Fallback: trim from the end until parseable (bounded effort)
  for (
    let i = input.length - 1, stop = Math.max(0, input.length - 4000);
    i > stop;
    i--
  ) {
    try {
      return normalizeParsedNodes(JSON.parse(input.slice(0, i)));
    } catch {}
  }

  return [];
}

// --- tolerant salvage that keeps as much as possible ---
function salvageJsonPrefix(input: string): string | null {
  let inString = false,
    esc = false;
  const stack: ('{' | '[')[] = [];
  let lastSafe = -1;
  let snapshot: ('{' | '[')[] = [];

  const isWS = (c: string) =>
    c === ' ' || c === '\t' || c === '\n' || c === '\r';

  const markSafe = (i: number) => {
    // allow commas after safe point
    lastSafe = i + 1;
    snapshot = stack.slice();
  };

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    if (inString) {
      if (esc) {
        esc = false;
      } else if (ch === '\\') {
        esc = true;
      } else if (ch === '"') {
        inString = false;
        markSafe(i);
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === '{' || ch === '[') {
      stack.push(ch);
      continue;
    }

    if (ch === '}' || ch === ']') {
      const want = ch === '}' ? '{' : '[';
      if (stack.length && stack[stack.length - 1] === want) {
        stack.pop();
        markSafe(i); // finished an object/array ⇒ safe
      } else break; // mismatch ⇒ stop
      continue;
    }

    // primitives (number/true/false/null)
    if (/[0-9tfn\-]/.test(ch)) {
      let j = i;
      while (j + 1 < input.length && /[0-9eE+\-.a-z]/.test(input[j + 1])) j++;
      markSafe(j);
      i = j;
    }
  }

  if (lastSafe < 0) return null;

  // Take prefix and trim any trailing commas/whitespace
  let out = input.slice(0, lastSafe);
  while (
    out.length &&
    (out[out.length - 1] === ',' || isWS(out[out.length - 1]))
  ) {
    out = out.slice(0, -1);
  }

  // Close any parents that were open at the last safe point
  for (let i = snapshot.length - 1; i >= 0; i--) {
    out += snapshot[i] === '{' ? '}' : ']';
  }
  return out;
}

// --- normalizers (keep ref as string[]) ---
function normalizeParsedNodes(x: unknown): INode[] {
  const arr = Array.isArray(x) ? x : [x];
  return arr.map(normalizeNode).filter((n): n is INode => !!n);
}

function normalizeNode(raw: any): INode | null {
  if (!raw || typeof raw !== 'object' || typeof raw.type !== 'string')
    return null;

  const node: INode = { type: raw.type as NodeType };

  if (typeof raw.text === 'string') node.text = raw.text;

  if (Array.isArray(raw.ref)) {
    node.ref = raw.ref.filter((v: any) => v != null).map(String);
  } else if (raw.ref != null) {
    node.ref = [String(raw.ref)];
  }

  if (Array.isArray(raw.children)) {
    node.children = raw.children
      .map(normalizeNode)
      .filter((c: any): c is INode => !!c);
  }

  return node;
}

/*
// Toggle this to force all refs to strings (you asked to coerce scalars to array of strings)
const COERCE_REFS_TO_STRINGS = true;

function stripTrailingCommas(jsonLike: string): string {
  return jsonLike.replace(/,\s*([}\]])/g, '$1').replace(/,\s*(\n|\r)*$/g, '');
}

function safeParse<T = any>(s: string): T | null {
  try {
    return JSON.parse(s);
  } catch {
    try {
      return JSON.parse(stripTrailingCommas(s));
    } catch {
      return null;
    }
  }
}

function normalizeRef(rawRef: unknown): Array<string | number> | undefined {
  // Accept: number|string|Array<number|string>; Output: Array<string|number>
  if (Array.isArray(rawRef)) {
    const vals = rawRef.filter(
      (v) => typeof v === 'string' || typeof v === 'number',
    ) as Array<string | number>;
    return COERCE_REFS_TO_STRINGS ? vals.map(String) : vals;
  }

  if (typeof rawRef === 'string' || typeof rawRef === 'number') {
    // Scalar -> array; and you asked to make scalars an array of strings
    return COERCE_REFS_TO_STRINGS ? [String(rawRef)] : [rawRef];
  }

  // null/undefined/other -> undefined
  return undefined;
}

function normalizeNode(raw: any): INode | null {
  if (!raw || typeof raw !== 'object') return null;

  const type: NodeType = NODE_TYPE_SET.has(raw.type) ? raw.type : 'div';
  const text = typeof raw.text === 'string' ? raw.text : undefined;
  const ref = normalizeRef(raw.ref);

  const children: INode[] | undefined = Array.isArray(raw.children)
    ? (raw.children as any[])
        .map(normalizeNode)
        .filter((c): c is INode => c !== null)
    : undefined;

  return { type, text, ref, children };
}

/** Tolerant, streaming-friendly parser (skips incomplete trailing object). * /
export function parseValidPromptBlocks(input: string): INode[] {
  const out: INode[] = [];

  const whole = safeParse<any>(input.trim());
  if (Array.isArray(whole)) {
    for (const item of whole) {
      const node = normalizeNode(item);
      if (node) out.push(node);
    }
    return out;
  }

  let buf = '';
  let depth = 0;
  let inString = false;
  let escaped = false;

  for (let i = 0; i < input.length; i++) {
    const ch = input[i];

    if (inString) {
      buf += ch;
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      buf += ch;
      continue;
    }

    if (ch === '{') {
      depth++;
      buf += ch;
      continue;
    }

    if (ch === '}') {
      depth--;
      buf += ch;

      if (depth === 0) {
        const candidate = safeParse<any>(buf);
        if (candidate) {
          const node = normalizeNode(candidate);
          if (node) out.push(node);
        }
        buf = '';
      }
      continue;
    }

    if (depth > 0) buf += ch;
  }

  // If depth > 0, last object was incomplete → intentionally ignored.
  return out;
}
*/
